import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { X, Search, Pencil, Trash2, Check, ChevronDown } from "lucide-react";
import {
  getThreads,
  renameThread,
  batchDeleteThreads,
} from "../../../../api/chat";

const PAGE_SIZE = 20;

const TABS = [
  { key: "all", label: "All" },
  { key: "private", label: "Private" },
  { key: "public", label: "Public" },
];

const formatRelativeTime = (ts) => {
  if (!ts) return "";
  const num = typeof ts === "string" ? parseInt(ts, 10) : ts;
  if (isNaN(num)) return "";
  const now = Math.floor(Date.now() / 1000);
  const diff = now - num;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  const d = new Date(num * 1000);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
};

const ThreadManager = ({ onClose }) => {
  const [threads, setThreads] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortNewestFirst, setSortNewestFirst] = useState(true);

  const [offsets, setOffsets] = useState({ private: 0, public: 0 });
  const [hasMore, setHasMore] = useState({ private: true, public: true });

  const renameInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchThreads = useCallback(
    async (reset = false) => {
      const privOffset = reset ? 0 : offsets.private;
      const pubOffset = reset ? 0 : offsets.public;
      const shouldFetchPriv = reset || hasMore.private;
      const shouldFetchPub = reset || hasMore.public;

      if (!shouldFetchPriv && !shouldFetchPub) return;

      if (reset) setLoading(true);
      else setLoadingMore(true);

      try {
        const promises = [];
        if (shouldFetchPriv) {
          promises.push(
            getThreads(privOffset, "private", debouncedSearch).then((res) => ({
              type: "private",
              threads: res?.data?.threads || [],
            })),
          );
        }
        if (shouldFetchPub) {
          promises.push(
            getThreads(pubOffset, "public", debouncedSearch).then((res) => ({
              type: "public",
              threads: res?.data?.threads || [],
            })),
          );
        }

        const results = await Promise.all(promises);
        const newOffsets = { ...offsets };
        const newHasMore = { ...hasMore };

        let incoming = [];
        for (const r of results) {
          incoming = incoming.concat(r.threads);
          if (r.type === "private") {
            newOffsets.private =
              (reset ? 0 : offsets.private) + r.threads.length;
            newHasMore.private = r.threads.length >= PAGE_SIZE;
          }
          if (r.type === "public") {
            newOffsets.public = (reset ? 0 : offsets.public) + r.threads.length;
            newHasMore.public = r.threads.length >= PAGE_SIZE;
          }
        }

        setOffsets(newOffsets);
        setHasMore(newHasMore);

        if (reset) {
          setThreads(incoming);
        } else {
          setThreads((prev) => {
            const existingIds = new Set(prev.map((t) => t.thread_id));
            const unique = incoming.filter(
              (t) => !existingIds.has(t.thread_id),
            );
            return [...prev, ...unique];
          });
        }
      } catch (e) {
        console.error("ThreadManager: failed to load threads:", e);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [offsets, hasMore, debouncedSearch],
  );

  useEffect(() => {
    setOffsets({ private: 0, public: 0 });
    setHasMore({ private: true, public: true });
    setSelectedIds(new Set());
    setConfirmDelete(false);

    const load = async () => {
      setLoading(true);
      try {
        const [privRes, pubRes] = await Promise.all([
          getThreads(0, "private", debouncedSearch),
          getThreads(0, "public", debouncedSearch),
        ]);
        const privThreads = privRes?.data?.threads || [];
        const pubThreads = pubRes?.data?.threads || [];
        setThreads([...privThreads, ...pubThreads]);
        setOffsets({ private: privThreads.length, public: pubThreads.length });
        setHasMore({
          private: privThreads.length >= PAGE_SIZE,
          public: pubThreads.length >= PAGE_SIZE,
        });
      } catch (e) {
        console.error("ThreadManager: failed to load threads:", e);
        setThreads([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [debouncedSearch]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const canLoadMore = hasMore.private || hasMore.public;
    if (!canLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && !loading) {
          fetchThreads(false);
        }
      },
      { rootMargin: "100px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, fetchThreads]);

  const filteredThreads = useMemo(() => {
    let list =
      activeTab === "all"
        ? threads
        : threads.filter((t) => (t.thread_type || "private") === activeTab);

    list = [...list].sort((a, b) => {
      const tsA = parseInt(a.last_chat, 10) || 0;
      const tsB = parseInt(b.last_chat, 10) || 0;
      return sortNewestFirst ? tsB - tsA : tsA - tsB;
    });

    return list;
  }, [threads, activeTab, sortNewestFirst]);

  const toggleSelect = useCallback((threadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
    setConfirmDelete(false);
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredThreads.map((t) => t.thread_id)));
    setConfirmDelete(false);
  }, [filteredThreads]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
    setConfirmDelete(false);
  }, []);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const startRename = useCallback((thread) => {
    setRenamingId(thread.thread_id);
    setRenameValue(thread.title || "");
  }, []);

  const commitRename = useCallback(async () => {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    try {
      await renameThread({ thread_id: renamingId, title: trimmed });
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === renamingId ? { ...t, title: trimmed } : t,
        ),
      );
      window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
    } catch (e) {
      console.error("Rename failed:", e);
    }
    setRenamingId(null);
  }, [renamingId, renameValue]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await batchDeleteThreads({ thread_ids: ids });
      setThreads((prev) => prev.filter((t) => !selectedIds.has(t.thread_id)));
      setSelectedIds(new Set());
      setConfirmDelete(false);
      window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
    } catch (e) {
      console.error("Bulk delete failed:", e);
    }
  }, [selectedIds, confirmDelete]);

  useEffect(() => {
    setConfirmDelete(false);
  }, [selectedIds.size]);

  useEffect(() => {
    const onRename = (e) => {
      const id = e.detail?.threadId;
      if (!id) return;
      const thread = threads.find((t) => t.thread_id === id);
      if (!thread) return;
      startRename(thread);
    };
    const onDelete = (e) => {
      const id = e.detail?.threadId;
      if (!id) return;
      if (!threads.find((t) => t.thread_id === id)) return;
      setSelectedIds(new Set([id]));
      setConfirmDelete(true);
    };
    window.addEventListener("chat-thread:start-rename", onRename);
    window.addEventListener("chat-thread:start-delete", onDelete);
    return () => {
      window.removeEventListener("chat-thread:start-rename", onRename);
      window.removeEventListener("chat-thread:start-delete", onDelete);
    };
  }, [threads, startRename]);

  useEffect(() => {
    if (searchInputRef.current) searchInputRef.current.focus();
  }, []);

  const selectedCount = selectedIds.size;
  const allFilteredSelected =
    filteredThreads.length > 0 &&
    filteredThreads.every((t) => selectedIds.has(t.thread_id));

  return (
    <div className="h-full flex flex-col font-poppins font-normal text-mainText">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-14 py-3 border-b border-mainDivider">
        <h2 className="text-sm font-medium text-mainText">Manage Threads</h2>
      </div>

      {/* Search */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-mutedText pointer-events-none"
            strokeWidth={2}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search threads…"
            className="w-full pl-8 pr-3 py-2.5 text-xs font-normal bg-transparent border border-inputBorder rounded-lg text-mainText placeholder:text-mutedText outline-none ring-inputFocus/60 focus:ring-2 transition"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-lg text-mutedText hover:text-mainText transition cursor-pointer"
            >
              <X className="h-3 w-3" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-x-1 px-4 pb-2 border-b border-mainDivider">
        {TABS.map(({ key, label }) => {
          const count =
            key === "all"
              ? threads.length
              : threads.filter((t) => (t.thread_type || "private") === key)
                  .length;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={[
                "px-2.5 py-1 rounded-lg text-xs font-normal transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2",
                activeTab === key
                  ? "bg-velBlue/10 text-velBlue"
                  : "text-mutedText hover:text-mainText hover:bg-mainButtonBg/50",
              ].join(" ")}
            >
              {label}
              <span className="ml-1 text-[0.625rem] opacity-60">{count}</span>
            </button>
          );
        })}

        {/* Sort toggle */}
        <button
          onClick={() => setSortNewestFirst((prev) => !prev)}
          className="ml-auto flex items-center gap-x-1 px-2 py-1 rounded-lg text-xs font-normal text-mutedText hover:text-mainText hover:bg-mainButtonBg/50 transition cursor-pointer"
          title={
            sortNewestFirst ? "Showing newest first" : "Showing oldest first"
          }
        >
          <ChevronDown
            className={`h-3 w-3 shrink-0 transition-transform ${sortNewestFirst ? "" : "rotate-180"}`}
            strokeWidth={2}
          />
          <span>{sortNewestFirst ? "Newest" : "Oldest"}</span>
        </button>
      </div>

      {/* Thread list */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-1"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-5 w-5 border-2 border-surfaceBorder border-t-velBlue rounded-full animate-spin" />
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xs font-normal text-mutedText">
              {debouncedSearch
                ? "No threads match your search"
                : "No threads yet"}
            </p>
          </div>
        ) : (
          <>
            {filteredThreads.map((thread) => {
              const isSelected = selectedIds.has(thread.thread_id);
              const isRenaming = renamingId === thread.thread_id;
              const threadType = thread.thread_type || "private";

              return (
                <div
                  key={thread.thread_id}
                  data-context-menu="chat-thread"
                  data-context-payload={JSON.stringify({
                    thread_id: thread.thread_id,
                    title: thread.title,
                    thread_type: thread.thread_type || "private",
                  })}
                  className={[
                    "group flex items-center gap-x-2.5 px-2 py-2 rounded-lg transition",
                    isSelected
                      ? "bg-velBlue/[0.05]"
                      : "hover:bg-mainButtonBg/50",
                  ].join(" ")}
                >
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleSelect(thread.thread_id)}
                    className={[
                      "w-4 h-4 rounded-lg border flex items-center justify-center shrink-0 transition cursor-pointer",
                      isSelected
                        ? "bg-velBlue border-velBlue"
                        : "border-inputBorder hover:border-velBlue/50",
                    ].join(" ")}
                  >
                    {isSelected && (
                      <Check
                        className="h-2.5 w-2.5 text-white"
                        strokeWidth={3}
                      />
                    )}
                  </button>

                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    {isRenaming ? (
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitRename();
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            cancelRename();
                          }
                        }}
                        onBlur={commitRename}
                        className="w-full text-xs font-normal bg-transparent border border-inputFocus/60 rounded-lg px-2 py-0.5 text-mainText outline-none focus:ring-2 focus:ring-inputFocus/60 transition"
                      />
                    ) : (
                      <span
                        onClick={() => {
                          window.dispatchEvent(
                            new CustomEvent("sidebar:chat-thread-selected", {
                              detail: {
                                threadId: thread.thread_id,
                                thread_type: thread.thread_type || "private",
                              },
                            }),
                          );
                        }}
                        className="block truncate text-xs font-normal text-mainText cursor-pointer hover:text-velBlue transition"
                      >
                        {thread.title || "Untitled"}
                      </span>
                    )}
                  </div>

                  {/* Type pill */}
                  {activeTab === "all" && !isRenaming && (
                    <span
                      className={[
                        "text-[0.5625rem] font-normal uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0",
                        threadType === "private"
                          ? "text-velBlue bg-velBlue/10"
                          : "text-errorText bg-errorText/10",
                      ].join(" ")}
                    >
                      {threadType}
                    </span>
                  )}

                  {/* Date */}
                  {!isRenaming && (
                    <span className="text-[0.625rem] font-normal text-mutedText shrink-0 w-14 text-right">
                      {formatRelativeTime(thread.last_chat)}
                    </span>
                  )}

                  {/* Actions */}
                  {!isRenaming && (
                    <div className="flex items-center gap-x-0.5 opacity-0 group-hover:opacity-100 transition shrink-0">
                      <button
                        onClick={() => startRename(thread)}
                        className="p-1 rounded-lg text-mutedText hover:text-velBlue hover:bg-velBlue/10 transition cursor-pointer"
                        title="Rename"
                      >
                        <Pencil className="h-3 w-3" strokeWidth={2} />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await batchDeleteThreads({
                              thread_ids: [thread.thread_id],
                            });
                            setThreads((prev) =>
                              prev.filter(
                                (t) => t.thread_id !== thread.thread_id,
                              ),
                            );
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              next.delete(thread.thread_id);
                              return next;
                            });
                            window.dispatchEvent(
                              new CustomEvent("sidebar:refresh-threads"),
                            );
                          } catch (e) {
                            console.error("Delete failed:", e);
                          }
                        }}
                        className="p-1 rounded-lg text-mutedText hover:text-errorText hover:bg-errorText/10 transition cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Infinite scroll sentinel */}
            <div
              ref={sentinelRef}
              className="h-8 flex items-center justify-center"
            >
              {loadingMore && (
                <div className="h-4 w-4 border-2 border-surfaceBorder border-t-velBlue rounded-full animate-spin" />
              )}
            </div>
          </>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-mainDivider bg-innerBg">
          <div className="flex items-center gap-x-3">
            <span className="text-xs font-normal text-mutedText">
              {selectedCount} selected
            </span>
            <button
              onClick={allFilteredSelected ? deselectAll : selectAll}
              className="text-xs font-normal text-velBlue hover:text-velBlue/80 transition cursor-pointer"
            >
              {allFilteredSelected ? "Deselect all" : "Select all"}
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            className={[
              "px-3 py-1 rounded-lg text-xs font-normal transition cursor-pointer",
              confirmDelete
                ? "bg-errorText text-white hover:bg-errorText/90"
                : "bg-errorText/10 text-errorText hover:bg-errorText/20",
            ].join(" ")}
          >
            {confirmDelete
              ? `Confirm delete ${selectedCount} thread${selectedCount === 1 ? "" : "s"}?`
              : `Delete ${selectedCount} thread${selectedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ThreadManager;
